import type { ReactNode } from 'react';
import {
  TEXT_FONT_FAMILY,
  TEXT_FONTS,
  TEXT_LINE_SPACING_PERCENT,
  TEXT_SIZE_SP,
  TEXT_SPEED_SP_PER_SECOND,
  plainToRichText,
  textWidgetStyle,
  type PortableTextOptions,
  type TextAlign,
  type TextFont,
  type TextVerticalAlign,
} from '@akarya/asp-client';
import { SchemaForm } from '../../components/SchemaForm.js';
import { RichTextEditor } from './RichTextEditor.js';
import type {
  Layout,
  LayoutAction,
  LayoutWidget,
  MediaItem,
  MenuBoardSummary,
  ModuleCatalogEntry,
} from '../../lib/types.js';

const MODULE_ICONS: Record<string, string> = {
  image: '🖼',
  video: '🎬',
  text: '𝐓',
  web: '🌐',
  clock: '🕒',
  menuboard: '🍽',
  weather: '⛅',
  rss: '📰',
  countdown: '⏳',
};

export function moduleIcon(slug: string): string {
  return MODULE_ICONS[slug] ?? '◆';
}

export function moduleLabel(slug: string, module?: ModuleCatalogEntry): string {
  return module?.name ?? slug.charAt(0).toUpperCase() + slug.slice(1);
}

/** One line describing what a widget shows, for compact playlist rows. */
export function widgetSummary(widget: LayoutWidget, media: MediaItem[], menuBoards: MenuBoardSummary[]): string {
  const options = widget.options ?? {};
  switch (widget.module) {
    case 'image':
    case 'video': {
      const sha = widget.media?.[0]?.sha256;
      if (!sha) return 'No media picked';
      const item = media.find((m) => m.sha256 === sha);
      return item?.originalName ?? sha.slice(0, 10);
    }
    case 'text':
      return typeof options.text === 'string' && options.text ? options.text : 'Empty text';
    case 'web':
      return typeof options.url === 'string' && options.url ? options.url : 'No URL';
    case 'clock':
      return typeof options.format === 'string' ? options.format : 'HH:mm';
    case 'menuboard':
      return menuBoards.find((b) => b.id === options.menuBoardId)?.name ?? 'No menu board picked';
    default:
      return '';
  }
}

/** Wraps a control in a label when the form is stacked (mobile); inline it stays bare. */
function Field({ label, stacked, children }: { label: string; stacked: boolean; children: ReactNode }): ReactNode {
  if (!stacked) return children;
  return (
    <label className="field">
      {label}
      {children}
    </label>
  );
}

const FONT_LABELS: Record<TextFont, string> = { SANS: 'Sans', SERIF: 'Serif', MONO: 'Mono', HANDWRITING: 'Script' };
const ALIGN_BUTTONS: { value: TextAlign; label: string; title: string }[] = [
  { value: 'START', label: '⇤', title: 'Align left' },
  { value: 'CENTER', label: '↔', title: 'Align center' },
  { value: 'END', label: '⇥', title: 'Align right' },
];
const VERTICAL_BUTTONS: { value: TextVerticalAlign; label: string; title: string }[] = [
  { value: 'TOP', label: '⤒', title: 'Align top' },
  { value: 'CENTER', label: '↕', title: 'Align middle' },
  { value: 'BOTTOM', label: '⤓', title: 'Align bottom' },
];
/** Where the size buttons step from on a widget still at the default size (4% of a 16:9 layout's width ≈ 38sp). */
const AUTO_SIZE_START_SP = 40;

/** − value + buttons, one tap per step, like the portable CMS's. */
function Stepper({
  label,
  value,
  onStep,
  canDown = true,
  canUp = true,
}: {
  label: string;
  value: string;
  onStep: (direction: 1 | -1) => void;
  canDown?: boolean;
  canUp?: boolean;
}): ReactNode {
  return (
    <span className="segmented" role="group" aria-label={label}>
      <button type="button" title={`${label}: less`} aria-label={`${label}: less`} disabled={!canDown} onClick={() => onStep(-1)}>
        −
      </button>
      <button type="button" disabled tabIndex={-1} className="stepper-value">
        {value}
      </button>
      <button type="button" title={`${label}: more`} aria-label={`${label}: more`} disabled={!canUp} onClick={() => onStep(1)}>
        +
      </button>
    </span>
  );
}

/** Editor for a widget's single `tap` action (ADR-0020) — navigate to another
 * layout (for interactive kiosks / menu boards) or go back. */
export function WidgetActionEditor({
  widget,
  layouts,
  currentLayoutId,
  onPatch,
  stacked = false,
}: {
  widget: LayoutWidget;
  layouts: Layout[];
  currentLayoutId: string;
  onPatch: (next: Partial<LayoutWidget>, key?: string) => void;
  stacked?: boolean;
}): ReactNode {
  const action = widget.actions?.[0];
  const kind = action?.type ?? 'none';
  const targets = layouts.filter((l) => l.id !== currentLayoutId);

  function setAction(next: LayoutAction | undefined, key?: string): void {
    onPatch({ actions: next ? [next] : [] }, key);
  }

  const kindSelect = (
    <select
      value={kind}
      onChange={(e) => {
        if (e.target.value === 'previousLayout') setAction({ trigger: 'tap', type: 'previousLayout' });
        else if (e.target.value === 'navigateLayout') {
          setAction({ trigger: 'tap', type: 'navigateLayout', layoutId: targets[0]?.id ?? '' });
        } else setAction(undefined);
      }}
    >
      <option value="none">nothing</option>
      <option value="navigateLayout">go to layout…</option>
      <option value="previousLayout">go back</option>
    </select>
  );

  return (
    <div className={stacked ? 'stack' : 'row wrap small widget-action-row'}>
      {stacked ? <Field label="On tap" stacked>{kindSelect}</Field> : <><span className="faint">on tap:</span>{kindSelect}</>}
      {kind === 'navigateLayout' ? (
        <>
          <Field label="Go to layout" stacked={stacked}>
            <select
              value={action?.layoutId ?? ''}
              onChange={(e) =>
                setAction({ trigger: 'tap', type: 'navigateLayout', layoutId: e.target.value, returnAfterS: action?.returnAfterS })
              }
            >
              <option value="">— pick layout —</option>
              {targets.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Return after (seconds)" stacked={stacked}>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={3600}
              title="Seconds before returning to the schedule (blank = default)"
              placeholder="auto-return"
              style={stacked ? undefined : { width: 90 }}
              value={action?.returnAfterS ?? ''}
              onChange={(e) =>
                setAction(
                  {
                    trigger: 'tap',
                    type: 'navigateLayout',
                    layoutId: action?.layoutId ?? '',
                    returnAfterS: e.target.value ? Number(e.target.value) : undefined,
                  },
                  `return:${widget.id}`,
                )
              }
            />
          </Field>
        </>
      ) : null}
    </div>
  );
}

/** Drops one property from a module's settingsSchema before handing the rest
 * to SchemaForm — used when a property already has its own dedicated picker. */
function withoutProperty(
  schema: Record<string, unknown> | null | undefined,
  key: string,
): Record<string, unknown> | null {
  if (!schema || typeof schema.properties !== 'object' || schema.properties === null) return schema ?? null;
  const properties = Object.fromEntries(
    Object.entries(schema.properties as Record<string, unknown>).filter(([k]) => k !== key),
  );
  const required = Array.isArray(schema.required) ? schema.required.filter((r) => r !== key) : schema.required;
  return { ...schema, properties, required };
}

export function WidgetOptions({
  widget,
  media,
  module,
  menuBoards,
  onPatch,
  stacked = false,
}: {
  widget: LayoutWidget;
  media: MediaItem[];
  module?: ModuleCatalogEntry;
  menuBoards: MenuBoardSummary[];
  /** `key` groups keystrokes in one field into a single undo step. */
  onPatch: (next: Partial<LayoutWidget>, key?: string) => void;
  /** Full-width labelled fields (the mobile sheet) instead of one compact row. */
  stacked?: boolean;
}): ReactNode {
  const options = widget.options ?? {};
  const setOption = (key: string, value: unknown): void =>
    onPatch({ options: { ...options, [key]: value } }, `${widget.id}:${key}`);
  const schemaChange = (next: Record<string, unknown>): void => onPatch({ options: next }, `${widget.id}:schema`);

  if (widget.module === 'menuboard') {
    return (
      <div className={stacked ? 'stack' : 'row wrap small'} style={stacked ? undefined : { flex: 1, gap: 8 }}>
        <label className={stacked ? 'field' : 'field small'}>
          Menu board
          <select value={String(options.menuBoardId ?? '')} onChange={(e) => setOption('menuBoardId', e.target.value)}>
            <option value="">— pick menu board —</option>
            {menuBoards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <SchemaForm
          schema={withoutProperty(module?.settingsSchema, 'menuBoardId')}
          value={options}
          onChange={schemaChange}
          stacked={stacked}
        />
      </div>
    );
  }

  if (module) {
    return <SchemaForm schema={module.settingsSchema} value={options} onChange={schemaChange} stacked={stacked} />;
  }

  if (widget.module === 'image' || widget.module === 'video') {
    const current = widget.media?.[0]?.sha256 ?? '';
    return (
      <div className={stacked ? 'stack' : 'row wrap small'}>
        <Field label={widget.module === 'image' ? 'Image' : 'Video'} stacked={stacked}>
          <select
            value={current}
            onChange={(e) => {
              const picked = media.find((m) => m.sha256 === e.target.value);
              onPatch({
                media: picked ? [{ sha256: picked.sha256, mime: picked.mime ?? undefined }] : [],
              });
            }}
          >
            <option value="">— pick media —</option>
            {media
              .filter((m) => m.mime?.startsWith(widget.module === 'image' ? 'image/' : 'video/'))
              .map((m) => (
                <option key={m.id} value={m.sha256}>
                  {m.originalName ?? m.sha256.slice(0, 10)}
                </option>
              ))}
          </select>
        </Field>
        {widget.module === 'image' ? (
          <Field label="Duration (seconds)" stacked={stacked}>
            <input
              type="number"
              inputMode="numeric"
              value={widget.duration ?? 10}
              title="seconds"
              style={stacked ? undefined : { width: 64 }}
              onChange={(e) => onPatch({ duration: Number(e.target.value) || 1 }, `${widget.id}:duration`)}
            />
          </Field>
        ) : (
          <label
            className="row small"
            style={{ gap: 4 }}
            title="Some players (web browsers, TVs) always play video muted"
          >
            <input type="checkbox" checked={options.muted === true} onChange={(e) => setOption('muted', e.target.checked)} />
            Muted
          </label>
        )}
      </div>
    );
  }

  if (widget.module === 'text') {
    // No backgroundColor = transparent (the runtime's default), so toggling it off just drops the key.
    const background = typeof options.backgroundColor === 'string' ? options.backgroundColor : undefined;
    const textColor = typeof options.color === 'string' ? options.color : '#ffffff';
    const setTransparent = (transparent: boolean): void => {
      const rest = { ...options };
      delete rest.backgroundColor;
      onPatch({ options: transparent ? rest : { ...rest, backgroundColor: '#000000' } });
    };
    // The portable CMS's text settings (asp-client textWidgetStyle); `key` groups repeated taps on one control.
    const text = textWidgetStyle(options);
    const portable = (
      typeof options.portableCms === 'object' && options.portableCms ? options.portableCms : {}
    ) as PortableTextOptions;
    const setPortable = (patch: PortableTextOptions, key: keyof PortableTextOptions): void =>
      onPatch({ options: { ...options, portableCms: { ...portable, ...patch } } }, `${widget.id}:portableCms.${key}`);
    const step = (value: number, range: { min: number; max: number; step: number }, direction: 1 | -1): number =>
      Math.min(range.max, Math.max(range.min, value + direction * range.step));
    const bold = text.bold ?? false;
    return (
      <div className={stacked ? 'stack' : 'row wrap small'} style={stacked ? undefined : { flex: 1, gap: 8 }}>
        <Field label="Type" stacked={stacked}>
          <span className="segmented" role="group" aria-label="Text type">
            <button type="button" aria-pressed={!text.running} onClick={() => setPortable({ running: false }, 'running')}>
              Static text
            </button>
            <button type="button" aria-pressed={text.running} onClick={() => setPortable({ running: true }, 'running')}>
              Running text
            </button>
          </span>
        </Field>
        <Field label="Text" stacked={stacked}>
          <RichTextEditor
            // A widget written before rich text has only `text`; start the editor from it.
            html={typeof options.html === 'string' ? options.html : plainToRichText(String(options.text ?? ''))}
            color={textColor}
            background={background}
            onChange={(html, plain) => onPatch({ options: { ...options, html, text: plain } }, `${widget.id}:html`)}
          />
        </Field>
        <Field label="Base text color" stacked={stacked}>
          <input
            type="color"
            title="Base text color (the toolbar colors selected words)"
            value={textColor}
            onChange={(e) => setOption('color', e.target.value)}
          />
        </Field>
        <Field label="Background" stacked={stacked}>
          <span className="row" style={{ gap: 6 }}>
            <input
              type="color"
              title="Background color"
              disabled={!background}
              value={background ?? '#000000'}
              onChange={(e) => setOption('backgroundColor', e.target.value)}
            />
            <label className="row small" style={{ gap: 4 }}>
              <input type="checkbox" checked={!background} onChange={(e) => setTransparent(e.target.checked)} />
              Transparent
            </label>
          </span>
        </Field>
        <Field label="Style" stacked={stacked}>
          <span className="segmented" role="group" aria-label="Style">
            <button
              type="button"
              title="Bold"
              aria-label="Bold"
              aria-pressed={bold}
              onClick={() => setPortable({ textBold: !bold }, 'textBold')}
            >
              <b>B</b>
            </button>
            <button
              type="button"
              title="Italic"
              aria-label="Italic"
              aria-pressed={text.italic}
              onClick={() => setPortable({ textItalic: !text.italic }, 'textItalic')}
            >
              <i>I</i>
            </button>
            <button
              type="button"
              title="Underline"
              aria-label="Underline"
              aria-pressed={text.underline}
              onClick={() => setPortable({ textUnderline: !text.underline }, 'textUnderline')}
            >
              <u>U</u>
            </button>
            <button
              type="button"
              title="Shadow"
              aria-label="Shadow"
              aria-pressed={text.shadow}
              onClick={() => setPortable({ textShadow: !text.shadow }, 'textShadow')}
            >
              <span style={{ textShadow: '0.1em 0.1em 0.12em rgba(0, 0, 0, 0.6)' }}>S</span>
            </button>
          </span>
        </Field>
        <Field label="Font" stacked={stacked}>
          <span className="segmented" role="group" aria-label="Font">
            {TEXT_FONTS.map((font) => (
              <button
                key={font}
                type="button"
                aria-pressed={text.font === font}
                // Each label in its own font, so the choice is visible before it's made.
                style={{ fontFamily: TEXT_FONT_FAMILY[font] }}
                onClick={() => setPortable({ textFont: font }, 'textFont')}
              >
                {FONT_LABELS[font]}
              </button>
            ))}
          </span>
        </Field>
        <Field label="Text size" stacked={stacked}>
          <Stepper
            label="Text size"
            value={text.sizeSp === undefined ? 'Auto' : `${text.sizeSp}sp`}
            canDown={(text.sizeSp ?? AUTO_SIZE_START_SP) > TEXT_SIZE_SP.min}
            canUp={(text.sizeSp ?? AUTO_SIZE_START_SP) < TEXT_SIZE_SP.max}
            onStep={(d) => setPortable({ textSizeSp: step(text.sizeSp ?? AUTO_SIZE_START_SP, TEXT_SIZE_SP, d) }, 'textSizeSp')}
          />
        </Field>
        {text.running ? (
          <>
            <Field label="Scroll speed" stacked={stacked}>
              <Stepper
                label="Scroll speed"
                value={String(text.speedSpPerSecond)}
                canDown={text.speedSpPerSecond > TEXT_SPEED_SP_PER_SECOND.min}
                canUp={text.speedSpPerSecond < TEXT_SPEED_SP_PER_SECOND.max}
                onStep={(d) =>
                  setPortable(
                    { textSpeedDpPerSecond: step(text.speedSpPerSecond, TEXT_SPEED_SP_PER_SECOND, d) },
                    'textSpeedDpPerSecond',
                  )
                }
              />
            </Field>
            <label
              className="row small"
              style={{ gap: 4 }}
              title={text.reverse ? 'Scrolls left to right' : 'Scrolls right to left'}
            >
              <input
                type="checkbox"
                checked={text.reverse}
                onChange={(e) => setPortable({ textReverse: e.target.checked }, 'textReverse')}
              />
              Reverse direction
            </label>
          </>
        ) : (
          <>
            <Field label="Alignment" stacked={stacked}>
              <span className="segmented" role="group" aria-label="Alignment">
                {ALIGN_BUTTONS.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    title={a.title}
                    aria-label={a.title}
                    aria-pressed={text.align === a.value}
                    onClick={() => setPortable({ textAlign: a.value }, 'textAlign')}
                  >
                    {a.label}
                  </button>
                ))}
              </span>
            </Field>
            <Field label="Vertical position" stacked={stacked}>
              <span className="segmented" role="group" aria-label="Vertical position">
                {VERTICAL_BUTTONS.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    title={a.title}
                    aria-label={a.title}
                    aria-pressed={text.verticalAlign === a.value}
                    onClick={() => setPortable({ textVerticalAlign: a.value }, 'textVerticalAlign')}
                  >
                    {a.label}
                  </button>
                ))}
              </span>
            </Field>
            <Field label="Line spacing" stacked={stacked}>
              <Stepper
                label="Line spacing"
                value={`${(text.lineSpacingPercent / 100).toFixed(1)}×`}
                canDown={text.lineSpacingPercent > TEXT_LINE_SPACING_PERCENT.min}
                canUp={text.lineSpacingPercent < TEXT_LINE_SPACING_PERCENT.max}
                onStep={(d) =>
                  setPortable(
                    { textLineSpacingPercent: step(text.lineSpacingPercent, TEXT_LINE_SPACING_PERCENT, d) },
                    'textLineSpacingPercent',
                  )
                }
              />
            </Field>
          </>
        )}
      </div>
    );
  }

  if (widget.module === 'web') {
    return (
      <Field label="Web address" stacked={stacked}>
        <input
          inputMode="url"
          value={String(options.url ?? '')}
          placeholder="https://…"
          onChange={(e) => setOption('url', e.target.value)}
        />
      </Field>
    );
  }

  if (widget.module === 'clock') {
    return (
      <Field label="Time format" stacked={stacked}>
        <input
          value={String(options.format ?? 'HH:mm')}
          placeholder="HH:mm"
          onChange={(e) => setOption('format', e.target.value)}
        />
      </Field>
    );
  }

  return <span className="faint small">no options</span>;
}
